import { Entity } from '@ecs/Entity';
import { Position } from '@components/Position';
import { Velocity } from '@components/Velocity';
import { Renderable, ShipShape } from '@components/Renderable';
import { Health } from '@components/Health';
import { Shield } from '@components/Shield';
import { Weapon } from '@components/Weapon';
import { StandardWeapon } from '@components/StandardWeapon';
import { RailgunWeapon } from '@components/RailgunWeapon';
import { TorpedoWeapon } from '@components/TorpedoWeapon';
import { PointDefense } from '@components/PointDefense';
import { PlayerInput } from '@components/PlayerInput';
import { AI } from '@components/AI';
import { HunterAI } from '@components/HunterAI';
import { SniperAI } from '@components/SniperAI';
import { CirclerAI } from '@components/CirclerAI';
import { Lifetime } from '@components/Lifetime';
import { Collider } from '@components/Collider';
import { Tag } from '@components/Tag';
import { Energy } from '@components/Energy';
import { BulletTrail } from '@components/BulletTrail';
import { ViewportEntry } from '@components/ViewportEntry';
import { TargetHighlight } from '@components/TargetHighlight';
import { WeaponRangeDisplay } from '@components/WeaponRangeDisplay';
import { HealthDisplay } from '@components/HealthDisplay';
import { ShieldDisplay } from '@components/ShieldDisplay';
import { ScoreValue } from '@components/ScoreValue';
import { Damage } from '@components/Damage';
import { AimTelegraph } from '@components/AimTelegraph';
import { MineLayer } from '@components/MineLayer';
import { CarrierBay } from '@components/CarrierBay';
import { EnemyType } from '@/levels/EnemyTypes';
import { ShipClass } from '@components/ShipClass';
import { Selectable } from '@components/Selectable';
import { ShieldProjector } from '@components/ShieldProjector';
import { PlayerShipClass, PLAYER_SHIP_INFO } from '@entities/PlayerShips';
import { tintColor } from '@utils/ColorUtils';

interface PlayerShipSpec {
  size: number;
  radius: number;
  maxSpeed: number;
  turnRate: number;
  health: number;
  shield: number;
  energy: number;
  energyRegen: number; // per second
  weapon: {
    cooldown: number;
    range: number;
    energyCost: number;
    bulletSpeed: number;
    damage: number;
    color: string;
    projectiles?: number;
    spread?: number;
  };
  pointDefense?: PointDefense;
}

const PLAYER_SHIP_SPECS: Record<PlayerShipClass, PlayerShipSpec> = {
  fighter: {
    size: 11, radius: 10, maxSpeed: 1.8, turnRate: 0.05, health: 3, shield: 2, energy: 100, energyRegen: 10,
    weapon: { cooldown: 200, range: 150, energyCost: 10, bulletSpeed: 8, damage: 1, color: '#00ffff' },
  },
  dart: {
    size: 10, radius: 8, maxSpeed: 2.6, turnRate: 0.08, health: 2, shield: 1, energy: 80, energyRegen: 12,
    weapon: { cooldown: 120, range: 120, energyCost: 5, bulletSpeed: 9, damage: 1, color: '#7fffff' },
  },
  gunship: {
    size: 14, radius: 12, maxSpeed: 1.2, turnRate: 0.035, health: 5, shield: 3, energy: 120, energyRegen: 14,
    weapon: { cooldown: 450, range: 170, energyCost: 15, bulletSpeed: 7, damage: 1, color: '#88aaff', projectiles: 3, spread: 0.35 },
  },
  longbow: {
    size: 13, radius: 10, maxSpeed: 1.5, turnRate: 0.04, health: 2, shield: 2, energy: 100, energyRegen: 12,
    weapon: { cooldown: 1400, range: 320, energyCost: 25, bulletSpeed: 14, damage: 3, color: '#c4b5ff' },
  },
  warden: {
    size: 12, radius: 11, maxSpeed: 1.5, turnRate: 0.045, health: 4, shield: 3, energy: 110, energyRegen: 12,
    weapon: { cooldown: 350, range: 130, energyCost: 8, bulletSpeed: 7, damage: 1, color: '#66fff0' },
    // range, burstCooldown, bulletCooldown, bulletsPerBurst
    pointDefense: new PointDefense(150, 900, 60, 8),
  },
};

interface EnemyHull {
  shape: ShipShape;
  color: string;
  strokeColor: string;
  size: number;
  radius: number;
  health: number;
  speed: [number, number];
  turnRate?: number;
  score: number;
  ramDamage?: number;
}

/** Full hull points of a player ship class. */
export function getPlayerShipMaxHull(shipClass: PlayerShipClass): number {
  return PLAYER_SHIP_SPECS[shipClass].health;
}

export class EntityFactory {
  static createPlayer(x: number, y: number): Entity {
    return EntityFactory.createPlayerShip('fighter', x, y);
  }

  static createPlayerShip(shipClass: PlayerShipClass, x: number, y: number): Entity {
    const spec = PLAYER_SHIP_SPECS[shipClass];
    const color = PLAYER_SHIP_INFO[shipClass].color;
    const weapon = spec.weapon;

    const ship = new Entity()
      .addComponent(new Position(x, y))
      .addComponent(new Velocity(0, 0, spec.maxSpeed, spec.turnRate))
      .addComponent(new Renderable(shipClass, color, spec.size, tintColor(color, 0.55), 1.5, color, 12))
      .addComponent(new Health(spec.health, spec.health))
      .addComponent(new Shield(spec.shield, spec.shield, 0.3, 3000)) // regenerates 0.3/sec after 3 sec
      .addComponent(new Energy(spec.energy, spec.energy, spec.energyRegen))
      .addComponent(new StandardWeapon(
        weapon.cooldown, weapon.range, weapon.energyCost, weapon.bulletSpeed, weapon.color,
        weapon.damage, 'auto', 0, weapon.projectiles ?? 1, weapon.spread ?? 0
      ))
      .addComponent(new Weapon(0, weapon.cooldown, weapon.range, weapon.energyCost, weapon.bulletSpeed, weapon.color, 'auto', weapon.damage)) // Keep for weapon mode switching
      .addComponent(spec.pointDefense ?? new PointDefense())
      .addComponent(new PlayerInput())
      .addComponent(new Collider(spec.radius, 'player', ['enemy', 'enemyBullet']))
      .addComponent(new Tag('player'))
      .addComponent(new ShipClass(shipClass))
      .addComponent(new Selectable())
      .addComponent(new WeaponRangeDisplay())
      .addComponent(new HealthDisplay())
      .addComponent(new ShieldDisplay(false, 0, spec.radius + 12, '#00ccff'));

    if (shipClass === 'warden') ship.addComponent(new ShieldProjector());
    return ship;
  }

  /** Creates the entities for one spawn of the given enemy type (swarmers arrive as a pack). */
  static createEnemyOfType(type: EnemyType, x: number, y: number): Entity[] {
    switch (type) {
      case 'standard': return [EntityFactory.createEnemy(x, y)];
      case 'railgun': return [EntityFactory.createRailgunEnemy(x, y)];
      case 'torpedo': return [EntityFactory.createTorpedoEnemy(x, y)];
      case 'swarmer': return EntityFactory.createSwarmerPack(x, y, 3);
      case 'dreadnought': return [EntityFactory.createDreadnought(x, y)];
      case 'aegis': return [EntityFactory.createAegis(x, y)];
      case 'minelayer': return [EntityFactory.createMinelayer(x, y)];
      case 'carrier': return [EntityFactory.createCarrier(x, y)];
    }
  }

  /** Components every enemy ship shares. */
  private static createEnemyHull(x: number, y: number, hull: EnemyHull): Entity {
    const [minSpeed, maxSpeed] = hull.speed;
    const speed = minSpeed + Math.random() * (maxSpeed - minSpeed);

    const entity = new Entity()
      .addComponent(new Position(x, y))
      .addComponent(new Velocity(Math.random() * Math.PI * 2, speed, speed, hull.turnRate ?? 0.1))
      .addComponent(new Renderable(hull.shape, hull.color, hull.size, hull.strokeColor, 1.5))
      .addComponent(new Health(hull.health, hull.health))
      .addComponent(new ViewportEntry())
      .addComponent(new Collider(hull.radius, 'enemy', ['player', 'playerBullet']))
      .addComponent(new Tag('enemy'))
      .addComponent(new TargetHighlight())
      .addComponent(new ScoreValue(hull.score));

    if (hull.health > 1) {
      entity.addComponent(new HealthDisplay(false, 1, '#00ff00', Math.max(18, hull.radius * 2.2), 3, hull.radius + 8));
    }
    if (hull.ramDamage) {
      entity.addComponent(new Damage(hull.ramDamage));
    }
    return entity;
  }

  static createEnemy(x: number, y: number): Entity {
    const shootCooldown = 3000 + Math.random() * 2000;

    return EntityFactory.createEnemyHull(x, y, {
      shape: 'interceptor', color: '#ff4444', strokeColor: '#ff9090',
      size: 9, radius: 8, health: 1, speed: [0.3, 1.3], score: 100,
    })
      .addComponent(new HunterAI())
      .addComponent(new StandardWeapon(shootCooldown, 250, 0, 4, '#ff4444', 1, 'auto'))
      .addComponent(new AI('hunt', 0, null, shootCooldown)); // Keep for shooting
  }

  static createRailgunEnemy(x: number, y: number): Entity {
    return EntityFactory.createEnemyHull(x, y, {
      shape: 'lancer', color: '#cc00ff', strokeColor: '#f080ff',
      size: 10, radius: 9, health: 2, speed: [0.25, 0.75], score: 200,
    })
      .addComponent(new RailgunWeapon())
      .addComponent(new Weapon(0, 4000, 300, 20, 15, '#ff00ff', 'auto', 2, 'railgun')) // Keep for compatibility
      .addComponent(new SniperAI())
      .addComponent(new AI('hunt', 0, null, 4000)) // Keep for shooting
      .addComponent(new WeaponRangeDisplay())
      .addComponent(new AimTelegraph());
  }

  static createTorpedoEnemy(x: number, y: number): Entity {
    return EntityFactory.createEnemyHull(x, y, {
      shape: 'bomber', color: '#ff6600', strokeColor: '#ffaa66',
      size: 10, radius: 8.5, health: 2, speed: [0.3, 1.0], score: 200,
    })
      .addComponent(new TorpedoWeapon())
      .addComponent(new SniperAI(350, 300, 400)) // Keep distance for torpedo range
      .addComponent(new AI('hunt', 0, null, 5000)); // Keep for compatibility
  }

  /** Fast kamikaze dart with no gun - it rams. */
  static createSwarmer(x: number, y: number): Entity {
    return EntityFactory.createEnemyHull(x, y, {
      shape: 'swarmer', color: '#ccff00', strokeColor: '#eeff99',
      size: 7, radius: 5.5, health: 1, speed: [2.0, 2.4], turnRate: 0.12, score: 50, ramDamage: 1,
    })
      .addComponent(new HunterAI(2000, 1.0))
      .addComponent(new AI('hunt'));
  }

  static createSwarmerPack(x: number, y: number, count: number): Entity[] {
    return Array.from({ length: count }, (_, i) => {
      const angle = (Math.PI * 2 * i) / count;
      return EntityFactory.createSwarmer(x + Math.cos(angle) * 14, y + Math.sin(angle) * 14);
    });
  }

  /** Slow battleship that holds mid range and fires wide volleys. */
  static createDreadnought(x: number, y: number): Entity {
    return EntityFactory.createEnemyHull(x, y, {
      shape: 'dreadnought', color: '#e0245e', strokeColor: '#ff8fb0',
      size: 20, radius: 17, health: 8, speed: [0.2, 0.35], turnRate: 0.03, score: 600, ramDamage: 2,
    })
      .addComponent(new SniperAI(210, 170, 250))
      .addComponent(new StandardWeapon(2400, 290, 0, 3.2, '#ff5c8a', 1, 'auto', 0, 5, 0.8))
      .addComponent(new AI('hunt', 0, null, 2400));
  }

  /** Shielded escort that circles its target while firing. */
  static createAegis(x: number, y: number): Entity {
    return EntityFactory.createEnemyHull(x, y, {
      shape: 'aegis', color: '#00e6b8', strokeColor: '#99ffe6',
      size: 12, radius: 11, health: 2, speed: [1.0, 1.3], turnRate: 0.06, score: 300,
    })
      .addComponent(new Shield(3, 3, 0.8, 2500))
      .addComponent(new ShieldDisplay(false, 0, 20, '#00ffcc'))
      .addComponent(new CirclerAI(170, 0.02, 0, Math.random() < 0.5 ? 1 : -1))
      .addComponent(new StandardWeapon(1800, 260, 0, 4.5, '#00ffcc', 1, 'auto'))
      .addComponent(new AI('circle', 0, null, 1800));
  }

  /** Orbits wide and leaves proximity mines in its wake. */
  static createMinelayer(x: number, y: number): Entity {
    return EntityFactory.createEnemyHull(x, y, {
      shape: 'minelayer', color: '#ffd11a', strokeColor: '#fff099',
      size: 12, radius: 11, health: 3, speed: [0.6, 0.8], turnRate: 0.05, score: 300,
    })
      .addComponent(new CirclerAI(260, 0.015, 0, Math.random() < 0.5 ? 1 : -1))
      .addComponent(new MineLayer())
      .addComponent(new AI('circle'));
  }

  /** Capital ship that hangs back and launches swarmer squadrons. */
  static createCarrier(x: number, y: number): Entity {
    return EntityFactory.createEnemyHull(x, y, {
      shape: 'carrier', color: '#8fa3b8', strokeColor: '#ccff66',
      size: 26, radius: 22, health: 14, speed: [0.2, 0.3], turnRate: 0.02, score: 1500, ramDamage: 3,
    })
      .addComponent(new SniperAI(380, 320, 440))
      .addComponent(new CarrierBay())
      .addComponent(new AI('hunt'));
  }

  static createMine(x: number, y: number, damage: number, lifetime: number): Entity {
    return new Entity()
      .addComponent(new Position(x, y))
      .addComponent(new Velocity(0, 0))
      .addComponent(new Renderable('mine', '#ffd11a', 5))
      .addComponent(new Lifetime(lifetime, lifetime))
      .addComponent(new Collider(8, 'enemyBullet', ['player', 'playerBullet']))
      .addComponent(new Tag('mine'))
      .addComponent(new Health(1, 1))
      .addComponent(new Damage(damage));
  }

  static createBullet(x: number, y: number, angle: number, speed: number, color: string, isPlayerBullet: boolean): Entity {
    return new Entity()
      .addComponent(new Position(x, y))
      .addComponent(new Velocity(angle, speed))
      .addComponent(new Renderable('bolt', color, 3, undefined, 0, color, 8))
      .addComponent(new Lifetime(150, 150))
      .addComponent(new Collider(8, isPlayerBullet ? 'playerBullet' : 'enemyBullet',
        isPlayerBullet ? ['enemy'] : ['player']))
      .addComponent(new Tag('bullet'))
      .addComponent(new BulletTrail());
  }

  static createParticle(x: number, y: number, angle: number, speed: number, color: string, lifetime: number): Entity {
    return new Entity()
      .addComponent(new Position(x, y))
      .addComponent(new Velocity(angle, speed))
      .addComponent(new Renderable('particle', color, 2))
      .addComponent(new Lifetime(lifetime, lifetime))
      .addComponent(new Tag('particle'));
  }
}
